import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import SuccessModal from './SuccessModal';
import i18n from '../../i18n';
import { createStep1Schema } from './validation/step1Schema';
import toast from 'react-hot-toast';

export default function MultiStepForm() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const topRef = useRef(null);

  const totalSteps = 4;
  const progress = Math.round(((step - 1) / totalSteps) * 100);

  useEffect(() => {
    if (topRef.current) {
      try {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [step]);

  const canNext = useMemo(() => {
    if (step === 1) {
      const selectedCountry = 'kg';
      const schema = createStep1Schema(t, selectedCountry);
      const isFreeEvent = (formData.docType || 'national') === 'national';
      const values = {
        fullName: formData.fullName || '',
        birthDate: formData.birthDate || '',
        gender: formData.gender || '',
        city: formData.city || null,
        email: formData.email || '',
        phone: formData.phone || '', // optional in schema
        isFreeEvent,
        passportImage: formData.passportImage || null,
        idFront: formData.idFront || null,
        idBack: formData.idBack || null,
      };
      return schema.isValidSync(values, { context: { touched: {} } });
    }
    if (step === 2) {
      const { category, subCategory, preferredAreas, otherLocation } = formData;
      const selected = Array.isArray(preferredAreas) ? preferredAreas : [];
      const hasPreferred = selected.length > 0;
      const needsOther = selected.includes('other');
      const otherOk = !needsOther || Boolean(String(otherLocation || '').trim());
      const categoryOk = Array.isArray(category) && category.length > 0;
      const subCategoryOk = Array.isArray(subCategory) && subCategory.length > 0;
      return Boolean(categoryOk && subCategoryOk && hasPreferred && otherOk);
    }
    if (step === 3) {
      const { story, skillLevel, performedBefore, readiness, videoUrl, videoAltMethods } = formData;
      const hasAlt = Array.isArray(videoAltMethods) && (videoAltMethods.includes('whatsapp') || videoAltMethods.includes('email'));
      const isUrl = (s) => {
        if (!s) return false;
        try { const u = new URL(s); return u.protocol === 'http:' || u.protocol === 'https:'; } catch (_) { return false; }
      };
      const videoOk = hasAlt ? true : isUrl(videoUrl);
      return Boolean((story || '').length >= 10 && skillLevel && performedBefore && readiness && videoOk);
    }
    if (step === 4) {
      const { agreementAccepted, referral, referralDetails, instagramProfile, tiktokProfile, youtubeProfile, snapchatProfile } = formData;
      const referralValid = referral !== 'yes' || Boolean((referralDetails || '').trim());
      const isUrl = (s) => {
        if (!s) return false;
        try { const u = new URL(s); return u.protocol === 'http:' || u.protocol === 'https:'; } catch (_) { return false; }
      };
      const socials = [instagramProfile, tiktokProfile, youtubeProfile, snapchatProfile];
      const socialsValid = socials.every((s) => !s || isUrl(s));
      return Boolean(agreementAccepted && referralValid && socialsValid);
    }
    return false;
  }, [step, formData, t]);

  const next = () => setStep((s) => Math.min(totalSteps, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));
  const resetForm = () => {
    setFormData({});
    setStep(1);
  };

  const submit = () => {
    setSubmitting(true);
    // Build payload as multipart/form-data and POST to backend
    const fd = new FormData();

    // Helpers
    const label = (lng, key) => i18n.t(key, { lng });
    const formatDMY = (ymd) => {
      if (!ymd) return '';
      const [y, m, d] = ymd.split('-');
      return `${d}-${m}-${y}`; // DD-MM-YYYY to match provided example
    };
    const sanitizePhone = (raw) => {
      const digits = String(raw || '').replace(/\D/g, '');
      if (!digits) return '';
      return digits.startsWith('966') ? `+${digits}` : `+966${digits}`;
    };

    // Step 1: basic info
    fd.append('full_name', formData.fullName || '');
    fd.append('date_of_birth', formatDMY(formData.birthDate));
    fd.append('mobile_number', sanitizePhone(formData.phone));
    fd.append('email', formData.email || '');

    // Gender en/ar
    if (formData.gender) {
      const gKey = formData.gender === 'male' ? 'registration.gender.male' : 'registration.gender.female';
      fd.append('gender[en]', label('en', gKey));
      fd.append('gender[ar]', label('ar', gKey));
    }

    // City en/ar
    if (formData.city?.value) {
      const cityKey = `cities.${formData.city.value}`;
      fd.append('city[en]', label('en', cityKey));
      fd.append('city[ar]', label('ar', cityKey));
    }

    // Identity files
    if (formData.passportImage?.file) {
      fd.append('passport', formData.passportImage.file);
    } else {
      if (formData.idFront?.file) fd.append('front_id', formData.idFront.file);
      if (formData.idBack?.file) fd.append('back_id', formData.idBack.file);
    }

    // Step 2: talent category and type (sub-category)
    const categories = Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : []);
    
    const cMap = {
      performingArts: 'registration.talent.categoryOptions.performingArts',
      visualFineArts: 'registration.talent.categoryOptions.visualFineArts',
      creativeDigital: 'registration.talent.categoryOptions.creativeDigital',
      culturalHandicrafts: 'registration.talent.categoryOptions.culturalHandicrafts',
      interactiveSports: 'registration.talent.categoryOptions.interactiveSports',
    };

    categories.forEach((cat, index) => {
      if (cat.value) {
        const cKey = cMap[cat.value];
        if (cKey) {
          fd.append(`talent_category[${index}][en]`, label('en', cKey));
          fd.append(`talent_category[${index}][ar]`, label('ar', cKey));
        }
      }
    });

    const subCategories = Array.isArray(formData.subCategory) ? formData.subCategory : (formData.subCategory ? [formData.subCategory] : []);
    
    const sMap = {
      // performing
      singingMusicPerformance: 'registration.talent.subCategoryOptions.performing.singingMusicPerformance',
      instrumentalPerformance: 'registration.talent.subCategoryOptions.performing.instrumentalPerformance',
      actingTheatre: 'registration.talent.subCategoryOptions.performing.actingTheatre',
      danceMovementShows: 'registration.talent.subCategoryOptions.performing.danceMovementShows',
      poetrySpokenWord: 'registration.talent.subCategoryOptions.performing.poetrySpokenWord',
      // visual fine arts
      paintingSculpture: 'registration.talent.subCategoryOptions.visualFineArts.paintingSculpture',
      photography: 'registration.talent.subCategoryOptions.visualFineArts.photography',
      digitalArtGraphicDesign: 'registration.talent.subCategoryOptions.visualFineArts.digitalArtGraphicDesign',
      liveArtDemo: 'registration.talent.subCategoryOptions.visualFineArts.liveArtDemo',
      // creative digital
      contentCreationVlogging: 'registration.talent.subCategoryOptions.creativeDigital.contentCreationVlogging',
      videoEditingDirecting: 'registration.talent.subCategoryOptions.creativeDigital.videoEditingDirecting',
      arVrExperiences: 'registration.talent.subCategoryOptions.creativeDigital.arVrExperiences',
      creativeCodingTechArt: 'registration.talent.subCategoryOptions.creativeDigital.creativeCodingTechArt',
      // cultural handicrafts
      handmadeCrafts: 'registration.talent.subCategoryOptions.culturalHandicrafts.handmadeCrafts',
      embroideryTailoring: 'registration.talent.subCategoryOptions.culturalHandicrafts.embroideryTailoring',
      traditionalPerformingArts: 'registration.talent.subCategoryOptions.culturalHandicrafts.traditionalPerformingArts',
      cooking: 'registration.talent.subCategoryOptions.culturalHandicrafts.cooking',
      // interactive sports
      sportsPerformances: 'registration.talent.subCategoryOptions.interactiveSports.sportsPerformances',
      magicAcrobatics: 'registration.talent.subCategoryOptions.interactiveSports.magicAcrobatics',
      eventHostingMc: 'registration.talent.subCategoryOptions.interactiveSports.eventHostingMc',
    };

    subCategories.forEach((sub, index) => {
      if (sub.value) {
        const sKey = sMap[sub.value];
        if (sKey) {
          fd.append(`talent_type[${index}][en]`, label('en', sKey));
          fd.append(`talent_type[${index}][ar]`, label('ar', sKey));
        }
      }
    });

    // Preferred areas -> suggested_locations[en][] and suggested_locations[ar][]
    const selectedAreas = Array.isArray(formData.preferredAreas) ? formData.preferredAreas : [];
    selectedAreas.forEach((val) => {
      const aKey = `registration.talent.preferredAreas.options.${val}`;
      fd.append('suggested_locations[en][]', label('en', aKey));
      fd.append('suggested_locations[ar][]', label('ar', aKey));
    });
    if (selectedAreas.includes('other') && String(formData.otherLocation || '').trim()) {
      fd.append('other_location', String(formData.otherLocation).trim());
    }

    // Step 3: performance details
    if (formData.story) fd.append('story', formData.story);

    if (formData.performedBefore) {
      const pKey = `registration.performance.performedBefore.options.${formData.performedBefore}`;
      fd.append('participated_before[en]', label('en', pKey));
      fd.append('participated_before[ar]', label('ar', pKey));
    }

    if (formData.skillLevel) {
      const sKey = `registration.performance.skillLevel.options.${formData.skillLevel}`;
      fd.append('skill_level[en]', label('en', sKey));
      fd.append('skill_level[ar]', label('ar', sKey));
    }

    if (formData.readiness) {
      const rKey = `registration.performance.readiness.options.${formData.readiness}`;
      fd.append('readiness[en]', label('en', rKey));
      fd.append('readiness[ar]', label('ar', rKey));
    }

    if (formData.videoUrl) fd.append('video_url', formData.videoUrl);
    (formData.videoAltMethods || []).forEach((m) => fd.append('video_alt_methods[]', m));

    if (Array.isArray(formData.financialExpectation)) {
      (formData.financialExpectation || []).forEach((val) => {
        const fKey = `registration.performance.financial.options.${val}`;
        fd.append('financial_expectation[en][]', label('en', fKey));
        fd.append('financial_expectation[ar][]', label('ar', fKey));
      });
    } else if (formData.financialExpectation) {
      const val = formData.financialExpectation;
      const fKey = `registration.performance.financial.options.${val}`;
      fd.append('financial_expectation[en][]', label('en', fKey));
      fd.append('financial_expectation[ar][]', label('ar', fKey));
    }

    // Step 4: final details
    if (formData.instagramProfile) fd.append('instagram_profile', formData.instagramProfile);
    if (formData.tiktokProfile) fd.append('tiktok_profile', formData.tiktokProfile);
    if (formData.youtubeProfile) fd.append('youtube_profile', formData.youtubeProfile);
    if (formData.snapchatProfile) fd.append('snapchat_profile', formData.snapchatProfile);

    if (formData.followersRange?.value) {
      const frKey = `registration.final.followersRange.options.${formData.followersRange.value}`;
      fd.append('followers_range[en]', label('en', frKey));
      fd.append('followers_range[ar]', label('ar', frKey));
    }

    if (formData.hearAboutUs?.value) {
      const hKey = `registration.final.hearAboutUs.options.${formData.hearAboutUs.value}`;
      fd.append('hear_about_us[en]', label('en', hKey));
      fd.append('hear_about_us[ar]', label('ar', hKey));
    }

    if (formData.referral) {
      const rKey = `registration.final.referral.options.${formData.referral}`;
      fd.append('referral[en]', label('en', rKey));
      fd.append('referral[ar]', label('ar', rKey));
    }
    if (formData.referralDetails) fd.append('referral_details', formData.referralDetails);

    fd.append('agreed', formData.agreementAccepted ? '1' : '0');

    fetch('https://api-php.hobbystation.co/api/talent-registration', {
      method: 'POST',
      body: fd,
      headers: {
        Accept: 'application/json',
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (data?.errors) {
            Object.values(data.errors).forEach((errorMessages) => {
              if (Array.isArray(errorMessages)) {
                errorMessages.forEach((msg) => toast.error(msg));
              }
            });
          } else if (data?.message) {
            toast.error(data.message);
          } else {
            toast.error(`Request failed (${res.status})`);
          }
          setSubmitting(false);
          return;
        }
        setShowSuccess(true);
        setSubmitting(false);
      })
      .catch((err) => {
        console.error('Submission failed:', err);
        toast.error('Submission failed. Please try again.');
        setSubmitting(false);
      });
  };

  return (
    <div className="min-h-screen !bg-[#3730510d] pt-8 pb-14 px-4">
      <div className="max-w-4xl mx-auto flex flex-col items-center">
        <div className='flex justify-center items-center gap-2 font-[500] text-[#151515] text-[14px] h-[38px] px-4 bg-[#FFFFFF] border-[1px] border-[#5D4A9D33] shadow-[0px_1px_2px_-1px_#0000001A] rounded-full max-w-[210px]'>
          <img src="/spark.svg" alt="" />
          {t('registration.page.badge')}
        </div>
        <h1 className='md:text-[36px] text-[26px] font-[700] mt-7 text-[#5D4A9D]'>
          {t('registration.page.title')}
        </h1>
        <p className='md:text-[20px] text-[16px] font-[500]  text-[#262626] mt-7 pb-5' >
          {t('registration.page.subtitle')}
        </p>


        <div ref={topRef} className="bg-white border-t-[4px] border-[1px] border-[#5D4A9D]  mt-5 rounded-[16px] shadow-[0px_20px_25px_-5px_#0000001A] w-full">
          <div className="flex flex-col items-center w-full">
            <div className='flex justify-center items-center gap-2 text-[20px]  font-[500]  text-[#262626] h-[38px] px-5 mt-7 bg-[linear-gradient(90deg,rgba(93,74,157,0.1)_0%,rgba(0,59,92,0.1)_100%)] border-[1px]  shadow-[0px_1px_2px_-1px_#0000001A] rounded-full w-fit'>
              {t('registration.page.formTitle')}
            </div>
            {/* <p className='text-[16px]  my-5 md:px-[30px] px-[20px] font-[500]  text-[#495057]'>
              {t('registration.page.formSubtitle')}
            </p> */}
          </div>
          <div className="flex items-center justify-end md:px-[30px] px-[20px] pt-5 py-0 ">
            {/* <div className="text-[#5D4A9D] text-sm font-semibold">Step {step} of {totalSteps}</div> */}
            <div className="text-[#495057] text-[14px]  ">{progress}% {t('registration.page.progressSuffix')}</div>
          </div>
          <div className="md:px-[30px] px-[20px] pt-2 ">
            <div className="w-full bg-[#EAE6F8] h-[6px] rounded-full">
              <div className="bg-[#5D4A9D] h-[6px] rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="md:px-[30px] px-[20px] pt-5 !pb-2">
            {step === 1 && <Step1 formData={formData} setFormData={setFormData} />}
            {step === 2 && <Step2 formData={formData} setFormData={setFormData} />}
            {step === 3 && <Step3 formData={formData} setFormData={setFormData} />}
            {step === 4 && <Step4 formData={formData} setFormData={setFormData} />}
          </div>
          <div className="flex items-center justify-between md:px-[30px] px-[20px] pb-3  mb-5 gap-4">
        {step !== 1 &&   <button
              className={`md:px-4 px-2 py-2 rounded-[8px] bg-[#5D4A9D1A] w-1/2 h-[48px] text-[#8456CD] font-[600]  text-[16px]   items-center flex justify-center gap-3 hover:bg-[#F3F4F6] `}
              onClick={prev}
              disabled={step === 1}
            >
              <img src="/prev.svg" alt="" />
              {t('registration.page.previous')}
            </button>}
            {step < totalSteps ? (
              <button
                className={`md:px-4 px-2 py-2 ${step !== 1 ? "w-1/2" : "w-full" } h-[48px] font-[600] rounded-[8px] text-[16px]   ${canNext ? 'bg-[#5D4A9D] text-white' : 'bg-[#5D4A9D] opacity-50 text-[#fff]'}`}
                onClick={next}
                disabled={!canNext}
              >
                {t('registration.page.next')}
              </button>
            ) : (              <>
              <button
                className={`md:px-4 md:block hidden  px-2 py-2 ${step !== 1 ? "w-1/2" : "w-full" } h-[48px] ${submitting ? 'bg-[#5D4A9D] opacity-50 text-[#fff]' : 'bg-[#5D4A9D] text-white' } font-[600] rounded-[8px] text-[16px]   ${canNext ? 'bg-[#5D4A9D] text-white' : 'bg-[#5D4A9D] opacity-50 text-[#fff]'}`}
                onClick={submit}
                disabled={!canNext || submitting}
              >
                {submitting ? t('registration.page.submitting') : t('registration.page.submit')}
              </button>
               <button
                className={`md:px-4 md:hidden  px-2 py-2 ${step !== 1 ? "w-1/2" : "w-full" } h-[48px] ${submitting ? 'bg-[#5D4A9D] opacity-50 text-[#fff]' : 'bg-[#5D4A9D] text-white' } font-[600] rounded-[8px] text-[16px]   ${canNext ? 'bg-[#5D4A9D] text-white' : 'bg-[#5D4A9D] opacity-50 text-[#fff]'}`}
                onClick={submit}
                disabled={!canNext || submitting}
              >
                {submitting ? t('registration.page.submittings') : t('registration.page.submits')}
              </button>
              </>
            )}
          </div>
        </div>
      </div>
      <SuccessModal open={showSuccess} onClose={() => { setShowSuccess(false); resetForm(); }} />
    </div>
  );
}